import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from '../hooks/useToast';
import { useBusy } from '../hooks/useBusy';
import { useAuthStore } from '../store/auth';
import { useI18n } from '../i18n';
import { useLabels, countLabel } from '../i18n/labels';
import { usePageTitle } from '../hooks/usePageTitle';
import { useListFrom, useUrlParams } from '../hooks/useUrlParam';
import FilterBar from '../components/shared/FilterBar';
import ModalBackdrop from '../components/shared/ModalBackdrop';
import ModalHeader from '../components/shared/ModalHeader';
import ModalFooter from '../components/shared/ModalFooter';
import CountryPicker from '../components/shared/CountryPicker';
import CountryDisplay from '../components/shared/CountryDisplay';
import { PeopleIcon, CheckIcon } from '../components/shared/Icons';
import Skeleton from '../components/shared/Skeleton';
import EmptyState from '../components/shared/EmptyState';
import AsyncContent from '../components/shared/AsyncContent';
import { useLoad } from '../hooks/useLoad';
import { useAfterWrite } from '../hooks/useAfterWrite';
import { getTeams, createTeam } from '../api/client';
import { countryFilterOptions } from '../lib/countries';
import { textMatches } from '../utils/personName';
import type { Team } from '../types';
import { BADGE_XS } from '../components/shared/badgeSize';
import Field from '../components/shared/Field';

/**
 * The list of the teams, at `/teams` (#595). A team opens on its own page,
 * `/teams/:id` (`TeamDetail.tsx`). Until 2026-10-14 one component held the
 * list and the team, and the URL stayed `/teams`.
 */
export default function TeamList() {
  const { hasRole } = useAuthStore();
  const { t, lang } = useI18n();
  const { roleLabel, athleteCount, teamCount, countryName } = useLabels();
  usePageTitle(t.nav.teams);
  // #552 — the counts of the team list in the plural form of the language.
  const memberCount = (n: number) => countLabel(lang, n, { one: t.teams.membersOne, few: t.teams.membersFew, other: t.teams.membersOther });
  const locationCount = (n: number) => countLabel(lang, n, { one: t.teams.locationsOne, few: t.teams.locationsFew, other: t.teams.locationsOther });
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [newTeam, setNewTeam] = useState({ name: '', location: '', country: 'LT', facebook_url: '' });
  const { showError } = useToast();
  // #605 — Create locks until the answer: a second tap sends no second POST.
  const creating = useBusy();
  // #457 — a failed load showed "No teams" instead of the failure.
  // #665, F02 — useLoad holds the loading, the error and Retry. The member teams come first.
  const { data: teams, loading: loadingTeams, error: teamsFailed, reload: reloadTeams, refresh: refreshTeams } = useLoad(
    () => getTeams().then(res => [...res.data].sort((a, b) => {
      const aIsMember = !!(a.my_role && a.my_status === 'approved');
      const bIsMember = !!(b.my_role && b.my_status === 'approved');
      if (aIsMember && !bIsMember) return -1;
      if (!aIsMember && bIsMember) return 1;
      return 0;
    })),
    [],
    { context: 'Teams:list', initial: [] as Team[] },
  );
  const afterWrite = useAfterWrite();
  const canCreateTeam = hasRole('admin', 'head_coach', 'team_manager');
  // #595 — the filters are in the URL, and each team link stores the URL of
  // the list: Back from a team shows the same filters and the same place.
  const listFrom = useListFrom();
  const [filters, setFilters] = useUrlParams({ q: '', location: '', country: '' });
  const { q: search, location: filterLocation, country: filterCountry } = filters;
  const setSearch = (v: string) => setFilters({ q: v });
  const setFilterLocation = (v: string) => setFilters({ location: v });
  const setFilterCountry = (v: string) => setFilters({ country: v });
  const clearFilters = () => setFilters({ q: '', location: '', country: '' });

  async function handleCreateTeam(e: React.FormEvent) {
    e.preventDefault();
    await creating.run(async () => {
      try {
        await createTeam(newTeam);
        setShowCreateTeam(false);
        setNewTeam({ name: '', location: '', country: 'LT', facebook_url: '' });
        // A failed reload keeps the list and shows a warning (#419).
        await afterWrite({ source: 'TeamList:create', success: t.teams.teamCreated, refresh: refreshTeams });
      } catch (err) { showError(err); }
    });
  }

  const locations = [...new Set(teams.map(c => c.location).filter((l): l is string => !!l))].sort();
  const filteredTeams = teams.filter(c => {
    // The name or the location, with no accent marks and no trailing space (#661, F30).
    if (search && !textMatches(search, c.name, c.location)) return false;
    if (filterLocation && c.location !== filterLocation) return false;
    if (filterCountry && c.country !== filterCountry) return false;
    return true;
  });

  const totalAthletes = teams.reduce((s, c) => s + (c.athlete_count || 0), 0);
  // #661 — teams exist and the filters hide every one: the list is not empty.
  const filtersHideAll = teams.length > 0 && filteredTeams.length === 0;

  return (
    <div className="space-y-4">
      {/* #552 — on a phone the subtitle wraps, and the button stays on one line. */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-black">{t.nav.teams}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{teamCount(teams.length)} · {athleteCount(totalAthletes)} · {locationCount(locations.length)}</p>
        </div>
        {canCreateTeam && <button onClick={() => setShowCreateTeam(true)} className="btn-primary btn-sm whitespace-nowrap shrink-0">+ {t.teams.createTeam}</button>}
      </div>

      {showCreateTeam && (
        <ModalBackdrop onClose={() => setShowCreateTeam(false)} maxWidth="sm" ariaLabel={t.teams.createTeam}>
          <ModalHeader title={t.teams.createTeam} />
          <form onSubmit={handleCreateTeam} className="space-y-3">
            <Field label={t.teams.teamName} required>
              <input data-autofocus className="input" value={newTeam.name} onChange={e => setNewTeam(f => ({ ...f, name: e.target.value }))} />
            </Field>
            <Field label={t.common.location}>
              <input className="input" value={newTeam.location} onChange={e => setNewTeam(f => ({ ...f, location: e.target.value }))} />
            </Field>
            <Field label={t.country.label}>
              <CountryPicker value={newTeam.country} onChange={code => setNewTeam(f => ({ ...f, country: code }))} />
            </Field>
            <Field label={t.teams.facebookPage}>
              <input type="url" className="input" placeholder="https://facebook.com/yourteam" value={newTeam.facebook_url}
                onChange={e => setNewTeam(f => ({ ...f, facebook_url: e.target.value }))} />
            </Field>
            <ModalFooter saveAsSubmit busy={creating.busy} saveLabel={t.common.create}
              onCancel={() => setShowCreateTeam(false)} />
          </form>
        </ModalBackdrop>
      )}

      <FilterBar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder={t.teams.searchTeams}
        selects={[
          {
            value: filterLocation,
            onChange: setFilterLocation,
            options: locations.map(l => ({ value: l, label: l })),
            placeholder: t.teams.allLocations,
          },
          {
            value: filterCountry,
            onChange: setFilterCountry,
            options: countryFilterOptions(teams.map(c => c.country), filterCountry, countryName),
            placeholder: t.country.all,
          },
        ]}
        showClear
        onClear={clearFilters}
        resultsCount={filteredTeams.length}
      />
      <AsyncContent
        loading={loadingTeams}
        error={!!teamsFailed}
        onRetry={reloadTeams}
        isEmpty={filteredTeams.length === 0}
        skeleton={<Skeleton.List shape="team-row" count={6} cols={3} />}
        empty={filtersHideAll ? (
          <EmptyState
            icon={<PeopleIcon className="w-12 h-12" />}
            title={t.teams.noTeamsMatch}
            action={
              <button type="button" data-testid="team-filter-clear" onClick={clearFilters} className="btn-outline btn-sm">
                {t.common.clear}
              </button>
            }
          />
        ) : <EmptyState
          icon={<PeopleIcon className="w-12 h-12" />}
          title={t.teams.noTeams}
          hint={t.common.empty.teamsHint}
        />}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filteredTeams.map(team => (
            <Link key={team.id} to={`/teams/${team.id}`} state={listFrom} data-testid="team-card"
              className={`block text-left card p-4 transition-all hover:shadow-md ${team.my_role ? 'border-l-4 border-green-500' : ''}`}>
              <div className="flex items-start gap-3">
                {team.logo_url
                  ? <img src={team.logo_url} alt={team.name} className="w-12 h-12 rounded-xl object-cover shrink-0" />
                  : <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center text-gray-400 shrink-0"><PeopleIcon className="w-7 h-7" /></div>}
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm truncate">{team.name}</div>
                  {team.location && <div className="text-xs text-gray-500 truncate">{team.location}</div>}
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {team.country && <CountryDisplay code={team.country} size="sm" />}
                    {team.is_federation_member && <span className={`badge ${BADGE_XS} bg-emerald-50 text-emerald-700 border-emerald-200`}>LMF</span>}
                    {/* #552 — the counts in the plural form of the language. member_count
                        is a bigint COUNT, so it arrives as a string: Number() first. */}
                    {Number(team.athlete_count ?? 0) > 0 && <span className={`badge badge-gray ${BADGE_XS}`}>{athleteCount(Number(team.athlete_count))}</span>}
                    {Number(team.member_count ?? 0) > 0 && <span className={`badge badge-gray ${BADGE_XS}`}>{memberCount(Number(team.member_count))}</span>}
                    {team.my_role && (
                      <span className={`badge ${BADGE_XS} bg-green-50 text-green-700 inline-flex items-center gap-0.5`}><CheckIcon className="w-2.5 h-2.5" /> {roleLabel(team.my_role)}</span>
                    )}
                    {team.my_status === 'pending' && !team.my_role && (
                      <span className={`badge badge-amber ${BADGE_XS}`}>{t.common.pending}</span>
                    )}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </AsyncContent>
    </div>
  );
}
